"use client";

import type { UtilityBarOffice } from "~www/types/utility-bar";

import MapThumbnail from "@homeinstead/ui/components/MapThumbnail";
import Link from "next/link";
import React from "react";
import { trackMapClick } from "~www/components/LocationSearchForm/events";
import { TrackedPhoneLink } from "../TrackedPhoneLink";

type OfficeDetailsProps = {
  office?: UtilityBarOffice;
  officePageURL?: string;
};

function OfficeDetails({ office, officePageURL }: OfficeDetailsProps) {
  if (!office?.address) {
    return null;
  }

  const address = office.address;
  const mapQuery = address
    ? [
        address.addressLine1,
        address.addressLine2,
        address.city,
        address.stateProvince,
        address.country,
      ]
        .filter(Boolean)
        .join(",")
    : "";
  const phone = office.phone ?? "";

  return (
    <div className="flex flex-row gap-4 border-b border-b-new-beginnings-base/25 p-4 md:gap-8 md:p-8">
      <div className="flex flex-1 flex-col gap-2 text-sm leading-6">
        <Link
          aria-label="Visit homeinstead franchise office"
          className="cursor-pointer font-bold text-md underline hover:no-underline"
          href={officePageURL ?? ""}
        >
          {address.city} Home Instead
        </Link>

        <div>
          <div className="max-w-60 truncate">{address.addressLine1}</div>
          <div>{`${address.city}, ${address.stateProvince} ${address.postalCode}`}</div>
        </div>

        {!phone ? null : (
          <span>
            Call us:{" "}
            <TrackedPhoneLink
              className="!px-0"
              phoneNumber={phone}
              placement="office details"
              variant="plain"
            />
          </span>
        )}
      </div>

      <a
        className="self-center md:self-auto"
        href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
        onClick={() => trackMapClick()}
        rel="noopener noreferrer"
        target="_blank"
      >
        <MapThumbnail
          addressLine1={address.addressLine1}
          addressLine2={address.addressLine2}
          city={address.city}
          className="h-full w-full object-cover"
          country={address.country}
          stateProvince={address.stateProvince}
        />
      </a>
    </div>
  );
}

OfficeDetails.displayName = "OfficeDetails";

export default OfficeDetails;
